import React from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { useWalletOptionDetails } from '@pkg/components';
import { type WalletConnectingData, type WalletConnectionStepError } from '@pkg/types';

import { CommonSolo } from '../../components';

import { ConnectionError } from './ConnectionError';

export interface MainConnectProps {
    type: WalletConnectingData['type'];
    step: WalletConnectingData['step'];
}

function assertErrorStep(step: WalletConnectingData['step']): step is WalletConnectionStepError {
    return step === 'tied-to-different-account' || step === 'missing-external-integration' || step === 'error';
}

export function MainConnect({ type, step }: MainConnectProps) {
    const [translate] = useTranslation('connectWallet');
    const { name, image } = useWalletOptionDetails(type);

    const errorMarkup = assertErrorStep(step) ? (
        <ConnectionError key={`ConnectionError-${step}`} errorStep={step} />
    ) : null;

    function StepContent() {
        switch (step) {
            case 'duplicate': {
                // TODO: If this is the content we want to use, then we
                // need to move it into `connect-wallet.json`.
                return (
                    <CommonSolo
                        title="No action required"
                        subtitle="This wallet is already connected to your Yat account."
                        avatarImg={image}
                    />
                );
            }
            case 'success': {
                // TODO: We shouldn't need this step. Instead, we
                // simply close the `ConnectWalletSheet` and reset state.
                return (
                    <CommonSolo
                        title="Success!"
                        subtitle="This wallet is now connected to your account."
                        avatarImg={image}
                    />
                );
            }
            case 'awaiting-external-action':
            case 'connecting':
            case 'tied-to-different-account':
            case 'missing-external-integration':
            case 'error':
            default: {
                const isConnecting = step === 'connecting';

                return (
                    <CommonSolo
                        title={translate(
                            isConnecting ? 'sheet-connection-title-connecting' : 'sheet-connection-title',
                            { name },
                        )}
                        subtitle={translate(
                            isConnecting ? 'sheet-connection-subtitle-connecting' : 'sheet-connection-subtitle',
                        )}
                        avatarImg={image}
                        loading={isConnecting}
                    >
                        <AnimatePresence mode="wait">{errorMarkup}</AnimatePresence>
                    </CommonSolo>
                );
            }
        }
    }

    return <StepContent />;
}
